import { ReactNode, useState } from 'react';
import { useSidebarOpenState } from '@backstage/core-components';
import { makeStyles, Collapse } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

// ---------------------------------------------------------------------------
// Collapsible nav sections, following modules/nav/reference/OmegaNav.tsx and
// re-expressed in the Quode tokens from theme/brand.css.
//
// The effects are all CSS-driven so nothing re-renders on hover:
//   · the section label's hairline rule lights up in emerald
//   · a +/− marker morphs by rotating one bar 90°
//   · items sit inside a panel with a vertical "guide rail" that fades at both
//     ends, brightens and glows on hover, and glows harder when the section
//     contains the active page (via :has)
//   · rows nudge 3px right on hover
//
// Every transform is suppressed under prefers-reduced-motion.
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  box: {},

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    margin: '16px 0 4px',
    padding: '3px 14px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--q-muted)',
    fontFamily: 'var(--q-font-mono)',
    transition: 'color var(--q-base) var(--q-ease)',
    '&:hover': { color: 'var(--q-emerald)' },
    '&:hover $rule': { background: 'rgba(16,185,129,0.45)' },
    '&:hover $plus': {
      borderColor: 'var(--q-emerald)',
      transform: 'scale(1.12)',
      boxShadow: '0 0 10px rgba(16,185,129,0.45)',
    },
    '&:focus-visible': { outline: '2px solid var(--q-emerald)', outlineOffset: 2, borderRadius: 8 },
    '@media (prefers-reduced-motion: reduce)': {
      '&:hover $plus': { transform: 'none' },
    },
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    flexShrink: 0,
  },
  rule: {
    flex: 1,
    height: 1,
    background: 'var(--q-border)',
    borderRadius: 1,
    transition: 'background var(--q-slow) var(--q-ease)',
  },

  // "+" that becomes "−": two bars, the vertical one rotates out of the way.
  plus: {
    position: 'relative',
    width: 16,
    height: 16,
    flexShrink: 0,
    borderRadius: '50%',
    border: '1px solid var(--q-border-strong)',
    transition:
      'border-color var(--q-slow) var(--q-ease), box-shadow var(--q-slow) var(--q-ease), transform var(--q-slow) var(--q-ease), background var(--q-slow) var(--q-ease)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 7,
      height: 1.6,
      transform: 'translate(-50%,-50%)',
      background: 'currentColor',
      borderRadius: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 1.6,
      height: 7,
      transform: 'translate(-50%,-50%)',
      background: 'currentColor',
      borderRadius: 1,
      transition: 'transform 280ms var(--q-spring)',
    },
    '@media (prefers-reduced-motion: reduce)': { '&::after': { transition: 'none' } },
  },
  plusOpen: {
    color: 'var(--q-emerald)',
    borderColor: 'rgba(16,185,129,0.45)',
    background: 'rgba(16,185,129,0.10)',
    '&::after': { transform: 'translate(-50%,-50%) rotate(90deg)' },
  },

  // Panel holding the section's rows, with the gradient guide rail.
  items: {
    position: 'relative',
    marginLeft: 14,
    paddingLeft: 15,
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: 12,
    transition: 'background var(--q-slow) var(--q-ease)',
    '&:hover': { background: 'rgba(241,245,249,0.035)' },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 1,
      top: 8,
      bottom: 8,
      width: 2,
      borderRadius: 2,
      background:
        'linear-gradient(to bottom, transparent 0%, rgba(16,185,129,0.22) 18%, rgba(16,185,129,0.22) 82%, transparent 100%)',
      transition: 'background var(--q-slow) var(--q-ease), box-shadow var(--q-slow) var(--q-ease)',
    },
    '&:hover::before': {
      background:
        'linear-gradient(to bottom, transparent 0%, var(--q-emerald) 18%, var(--q-emerald) 82%, transparent 100%)',
      boxShadow: '0 0 8px rgba(16,185,129,0.40)',
    },
    // Brighten when this section holds the current page. Backstage marks the
    // selected sidebar item with a class containing "selected".
    '&:has([aria-current="page"], [class*="selected"], [class*="Selected"])::before': {
      background:
        'linear-gradient(to bottom, transparent 0%, var(--q-emerald) 12%, var(--q-emerald) 88%, transparent 100%)',
      boxShadow: '0 0 10px rgba(16,185,129,0.50)',
    },
    '& > a, & > div': { transition: 'transform var(--q-base) var(--q-ease)' },
    '& > a:hover, & > div:hover': { transform: 'translateX(3px)' },
    '@media (prefers-reduced-motion: reduce)': {
      '& > a:hover, & > div:hover': { transform: 'none' },
    },
  },

  // Collapsed rail: the label cannot be shown, so the grouping is a hairline.
  railRule: {
    margin: '14px 12px 6px',
    height: 1,
    background: 'var(--q-border)',
  },
});

/**
 * Collapsible section heading. Open/closed state persists per section so the
 * nav looks the same on the next visit.
 */
export const NavSection = ({
  label,
  children,
  storageKey,
  defaultOpen = true,
}: {
  label: string;
  children?: ReactNode;
  /** localStorage key; omit to make the section non-persistent. */
  storageKey?: string;
  defaultOpen?: boolean;
}) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const key = storageKey ?? `quodin.nav.${label.toLowerCase().replace(/\s+/g, '-')}`;

  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      if (v != null) return v === '1';
    } catch {
      // Private browsing can throw on access; fall back to the default.
    }
    return defaultOpen;
  });

  const toggle = () =>
    setOpen(prev => {
      const next = !prev;
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch {
        // Persistence is a convenience, not a requirement.
      }
      return next;
    });

  // On the collapsed icon rail there is no room for a label or a toggle.
  if (!isOpen) {
    return <div className={classes.railRule} aria-hidden />;
  }

  // Without children this is just a heading (the nav renders items as siblings).
  if (!children) {
    return (
      <div className={classes.box}>
        <button
          type="button"
          className={classes.header}
          onClick={toggle}
          aria-expanded={open}
        >
          <span className={classes.label}>{label}</span>
          <span className={classes.rule} aria-hidden />
          <span
            className={`${classes.plus} ${open ? classes.plusOpen : ''}`}
            aria-hidden
          />
        </button>
      </div>
    );
  }

  return (
    <div className={classes.box}>
      <button
        type="button"
        className={classes.header}
        onClick={toggle}
        aria-expanded={open}
      >
        <span className={classes.label}>{label}</span>
        <span className={classes.rule} aria-hidden />
        <span
          className={`${classes.plus} ${open ? classes.plusOpen : ''}`}
          aria-hidden
        />
      </button>
      <Collapse in={open} timeout={260} unmountOnExit>
        <div className={classes.items}>{children}</div>
      </Collapse>
    </div>
  );
};

// ---------------------------------------------------------------------------
// External tool link, styled to match a SidebarItem, with a live status dot.
// ---------------------------------------------------------------------------

const useItemStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 40,
    padding: '0 12px',
    marginBottom: 2,
    borderRadius: 10,
    color: 'var(--q-sub)',
    textDecoration: 'none',
    transition: 'background var(--q-fast) var(--q-ease), color var(--q-fast) var(--q-ease)',
    '&:hover': {
      background: 'rgba(241,245,249,0.06)',
      color: 'var(--q-text)',
      textDecoration: 'none',
    },
    '&:hover $icon': { color: 'var(--q-text)' },
    '&:hover $launch': { opacity: 0.6 },
    '&:focus-visible': { outline: '2px solid var(--q-emerald)', outlineOffset: -2 },
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
    width: 22,
    justifyContent: 'center',
    color: 'var(--q-muted)',
    transition: 'color var(--q-fast) var(--q-ease)',
  },
  text: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  launch: {
    fontSize: 14,
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity var(--q-base) var(--q-ease)',
  },
  dot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
});

export type ToolStatus = 'up' | 'down' | 'unknown';

const statusColor: Record<ToolStatus, string> = {
  up: 'var(--q-emerald)',
  down: 'var(--q-red)',
  unknown: 'var(--q-slate)',
};

export const ExternalNavItem = ({
  href,
  text,
  icon,
  status = 'unknown',
}: {
  href: string;
  text: string;
  icon: ReactNode;
  status?: ToolStatus;
}) => {
  const classes = useItemStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.root}
      title={isOpen ? undefined : text}
    >
      <span className={classes.icon}>{icon}</span>
      {isOpen && (
        <>
          <span className={classes.text}>{text}</span>
          <OpenInNewIcon className={classes.launch} />
          <span
            className={classes.dot}
            style={{
              background: statusColor[status],
              boxShadow: status === 'up' ? '0 0 6px var(--q-emerald)' : undefined,
              animation:
                status === 'up' ? 'q-breathe 3s ease-in-out infinite' : undefined,
            }}
            aria-label={`status ${status}`}
          />
        </>
      )}
    </a>
  );
};
